import {
  IonContent,
  IonPage,
  IonButton,
  IonCard,
  IonCardContent,
  IonSpinner,
} from '@ionic/react';
import { useState, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { useWallet } from '../contexts/WalletContext';
import AppHeader from '../components/AppHeader';
import BottomNav from '../components/BottomNav';
import { playCelebrationSound, playWheelTurningSound, stopWheelTurningSound } from '../utils/soundUtils';
import './SpinWheel.css';

interface WheelSegment {
  id: number;
  label: string;
  value: number;
  color: string;
}

const SpinWheel: React.FC = () => {
  const { earnCoins } = useWallet();
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<WheelSegment | null>(null);
  const [canSpin, setCanSpin] = useState(true);
  const [lastSpinTime, setLastSpinTime] = useState<number | null>(null);
  const [currentWheelRotation, setCurrentWheelRotation] = useState(0); // Track current wheel rotation
  const wheelSoundRef = useRef<AudioBufferSourceNode | null>(null); // Reference to wheel turning sound

  // Confetti celebration function
  const triggerConfetti = () => {
    // Play celebration sound
    playCelebrationSound();
    
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    const interval: NodeJS.Timeout = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      
      // Launch confetti from both sides
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);
  };

  // Wheel segments with different coin values (12 segments total - 30 degrees each)
  // Distribution: 4 x 5 coins, 4 x 10 coins, 1 x 25, 1 x 50, 1 x 100, 1 x 200
  // Arranged so pairs face each other (180 degrees apart) and no same values are neighbors:
  // - 200 and 100 are opposite (positions 0 and 6)
  // - 50 and 25 are opposite (positions 1 and 7)
  // - 5s and 10s alternate and are paired opposite each other
  // Same values have the same color
  const segments: WheelSegment[] = [
    { id: 1, label: '200', value: 200, color: '#FFD700' }, // Position 0 - Bright Gold for 200
    { id: 2, label: '50', value: 50, color: '#FF6B35' },   // Position 1 - Vibrant Orange for 50
    { id: 3, label: '5', value: 5, color: '#2196F3' },    // Position 2 - Bright Blue for 5s
    { id: 4, label: '10', value: 10, color: '#FF4081' },  // Position 3 - Bright Magenta for 10s
    { id: 5, label: '5', value: 5, color: '#2196F3' },     // Position 4 - Bright Blue for 5s
    { id: 6, label: '10', value: 10, color: '#FF4081' },  // Position 5 - Bright Magenta for 10s
    { id: 7, label: '100', value: 100, color: '#00E676' }, // Position 6 - Bright Green for 100 (opposite 200)
    { id: 8, label: '25', value: 25, color: '#FF5722' },  // Position 7 - Bright Deep Orange for 25 (opposite 50)
    { id: 9, label: '5', value: 5, color: '#2196F3' },    // Position 8 - Bright Blue for 5s (opposite position 2)
    { id: 10, label: '10', value: 10, color: '#FF4081' }, // Position 9 - Bright Magenta for 10s (opposite position 3)
    { id: 11, label: '5', value: 5, color: '#2196F3' },   // Position 10 - Bright Blue for 5s (opposite position 4)
    { id: 12, label: '10', value: 10, color: '#FF4081' }, // Position 11 - Bright Magenta for 10s (opposite position 5)
  ];

  // For testing: Set to 0 for unlimited spins, or use a short cooldown like 1000 (1 second)
  const SPIN_COOLDOWN = 0; // 0 = unlimited spins for testing (change to 24 * 60 * 60 * 1000 for production)

  useEffect(() => {
    // Check if user can spin based on cooldown
    if (SPIN_COOLDOWN === 0) {
      setCanSpin(true);
      return;
    }
    
    const lastSpin = localStorage.getItem('lastSpinTime');
    if (lastSpin) {
      const timeSinceLastSpin = Date.now() - parseInt(lastSpin);
      if (timeSinceLastSpin < SPIN_COOLDOWN) {
        setCanSpin(false);
        setLastSpinTime(parseInt(lastSpin));
      } else {
        setCanSpin(true);
      }
    }
  }, []);

  const getTimeRemaining = (): string => {
    if (!lastSpinTime || SPIN_COOLDOWN === 0) return '';
    const remaining = SPIN_COOLDOWN - (Date.now() - lastSpinTime);
    if (remaining <= 0) {
      setCanSpin(true);
      return '';
    }
    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const resetCooldown = () => {
    localStorage.removeItem('lastSpinTime');
    setLastSpinTime(null);
    setCanSpin(true);
  };

  // Cleanup: Stop wheel sound if component unmounts while spinning
  useEffect(() => {
    return () => {
      if (wheelSoundRef.current) {
        stopWheelTurningSound(wheelSoundRef.current);
        wheelSoundRef.current = null;
      }
    };
  }, []);

  const spinWheel = async () => {
    if (spinning || !canSpin) return;

    setSpinning(true);
    setResult(null);
    
    // Start wheel turning sound
    wheelSoundRef.current = playWheelTurningSound();

    // Pre-select a random segment to win - this ensures the reward matches the visual
    const randomIndex = Math.floor(Math.random() * segments.length);
    const selectedSegment = segments[randomIndex];
    
    const segmentAngle = 360 / segments.length; // 30 degrees for 12 segments
    
    // Add multiple full rotations for visual effect
    const spinRotations = 5 + Math.random() * 3; // 5-8 full rotations
    
    // Calculate rotation to align the selected segment's CENTER with the pointer (0 degrees)
    // The pointer is fixed at the top (0 degrees)
    // Each segment spans: segmentStartAngle to segmentStartAngle + segmentAngle
    // Segment center is at: segmentStartAngle + segmentAngle/2
    // 
    // When wheel rotates clockwise by R degrees via CSS transform:
    // - The wheel rotates clockwise, so a point originally at angle A appears at (A + R) mod 360
    // - We want the segment center to end up at 0° (where the pointer is)
    // - So: (segmentCenterAngle + totalRotation) % 360 = 0
    // - Therefore: totalRotation = (360 - segmentCenterAngle) + (full spins * 360)
    //
    // We need to animate FROM currentWheelRotation TO totalRotation
    // So the animation will be: rotate from currentWheelRotation to totalRotation
    const segmentCenterAngle = randomIndex * segmentAngle + segmentAngle / 2;
    
    // Calculate how much we need to rotate from current position to align segment with pointer
    // Normalize current rotation to 0-360 for calculation
    const currentNormalized = ((currentWheelRotation % 360) + 360) % 360;
    
    // The target rotation (from 0) to align this segment with pointer (0 degrees)
    const targetRotationFromZero = (360 - segmentCenterAngle) % 360;
    
    // Calculate how much we need to rotate from current position to reach target
    let rotationNeeded = 0;
    if (targetRotationFromZero >= currentNormalized) {
      rotationNeeded = targetRotationFromZero - currentNormalized;
    } else {
      // Target is behind current, so we need to go around
      rotationNeeded = (360 - currentNormalized) + targetRotationFromZero;
    }
    
    // Add full spins for visual effect (always at least 5-8 spins)
    const minSpins = Math.ceil(spinRotations);
    const totalRotation = currentWheelRotation + rotationNeeded + minSpins * 360;
    
        // Log spin calculation (only in development - check if not production)
        const isDev = !(import.meta as any).env?.PROD;
        if (isDev) {
          console.log('Spin calculation:', {
            randomIndex,
            selectedValue: selectedSegment.value,
            segmentCenterAngle,
            currentWheelRotation,
            currentNormalized,
            targetRotationFromZero,
            rotationNeeded,
            minSpins,
            totalRotation,
            rotationAmount: totalRotation - currentWheelRotation,
            finalPosition: (segmentCenterAngle + totalRotation) % 360
          });
        }
    
    // Update the wheel rotation
    const wheel = document.getElementById('wheel');
    if (wheel) {
      wheel.style.transition = 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)';
      wheel.style.transform = `rotate(${totalRotation}deg)`;
    }

    // Wait for animation to complete
    setTimeout(async () => {
      // Stop wheel turning sound
      stopWheelTurningSound(wheelSoundRef.current);
      wheelSoundRef.current = null;
      
      // Update the tracked rotation - keep the actual total rotation value (not normalized)
      // This ensures we always calculate the next spin correctly from the current position
      setCurrentWheelRotation(totalRotation);
      
      // Use the pre-selected segment - this matches what the user sees
      setResult(selectedSegment);
      setSpinning(false);

      // Trigger confetti celebration
      triggerConfetti();

      // Award coins based on the pre-selected segment
      try {
        await earnCoins(selectedSegment.value, `Spin the Wheel - Won ${selectedSegment.value} coins`, 'spin-wheel');
      } catch (error) {
        console.error('Error awarding coins:', error);
      }

      // Set cooldown (only if cooldown is enabled)
      if (SPIN_COOLDOWN > 0) {
        const now = Date.now();
        localStorage.setItem('lastSpinTime', now.toString());
        setLastSpinTime(now);
        setCanSpin(false);
      }
    }, 4000);
  };

  return (
    <IonPage>
      <AppHeader title="Spin the Wheel" showBackButton={true} defaultHref="/games" />
      <IonContent fullscreen className="ion-padding">
        <div className="spin-wheel-container">
          <IonCard>
            <IonCardContent>
              <h2 className="game-title">🎡 Spin the Wheel</h2>
              <p className="game-description">
                Spin and win coins! Each spin has a chance to win between 5 and 200 coins.
              </p>
            </IonCardContent>
          </IonCard>

          {/* Wheel Container */}
          <div className="wheel-container">
            <div className="wheel-wrapper">
              <div id="wheel" className="wheel">
                <div className="wheel-segments">
                  {segments.map((segment, index) => {
                    const segmentAngle = 360 / segments.length; // 30 degrees for 12 segments
                    const segmentStartAngle = index * segmentAngle;
                    // Position label at the CENTER of the segment (not at the border)
                    const segmentCenterAngle = segmentStartAngle + segmentAngle / 2;
                    // Calculate position on the wheel (center is at 50%, 50%)
                    // Convert angle to radians, with 0 degrees at top (so subtract 90)
                    const centerRad = ((segmentCenterAngle - 90) * Math.PI) / 180;
                    // Position label closer to the edge (increase radius)
                    // 45% from center = closer to edge while still within the wheel
                    const radius = 45; // percentage from center (increased from 35)
                    const labelX = 50 + radius * Math.cos(centerRad);
                    const labelY = 50 + radius * Math.sin(centerRad);
                    
                    return (
                      <span
                        key={segment.id}
                        className="segment-label"
                        style={{
                          position: 'absolute',
                          left: `${labelX}%`,
                          top: `${labelY}%`,
                          transform: `translate(-50%, -50%) rotate(${segmentCenterAngle}deg)`,
                        } as React.CSSProperties}
                      >
                        {segment.label}
                      </span>
                    );
                  })}
                </div>
                <div className="wheel-center">🪙</div>
              </div>
              <div className="wheel-pointer"></div>
            </div>
          </div>

          {/* Spin Button */}
          <div className="spin-controls">
            {canSpin ? (
              <IonButton
                expand="block"
                color="primary"
                size="large"
                onClick={spinWheel}
                disabled={spinning}
                className="spin-button"
              >
                {spinning ? (
                  <>
                    <IonSpinner name="crescent" />
                    <span style={{ marginLeft: '10px' }}>Spinning...</span>
                  </>
                ) : (
                  'Spin the Wheel!'
                )}
              </IonButton>
            ) : (
              <IonCard>
                <IonCardContent>
                  <p style={{ textAlign: 'center', margin: 0 }}>
                    ⏰ Next spin available in: <strong>{getTimeRemaining()}</strong>
                  </p>
                  <p style={{ textAlign: 'center', fontSize: '0.9rem', color: 'var(--ion-text-color-step-600)', marginTop: '0.5rem' }}>
                    You can spin once every 24 hours
                  </p>
                  <IonButton
                    expand="block"
                    fill="clear"
                    size="small"
                    onClick={resetCooldown}
                    style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}
                  >
                    🔄 Reset Cooldown (Testing)
                  </IonButton>
                </IonCardContent>
              </IonCard>
            )}
          </div>

          {/* Prize Info */}
          <IonCard>
            <IonCardContent>
              <h3>Possible Prizes</h3>
              <div className="prize-list">
                {Array.from(new Map(segments.map(segment => [segment.value, segment])).values())
                  .sort((a, b) => a.value - b.value)
                  .map((segment) => (
                    <div key={segment.value} className="prize-item" style={{ borderLeftColor: segment.color }}>
                      <span className="prize-value">{segment.value} coins</span>
                    </div>
                  ))}
              </div>
            </IonCardContent>
          </IonCard>
        </div>
      </IonContent>
      <BottomNav />
    </IonPage>
  );
};

export default SpinWheel;

